import type { PhaseNineDto, ProcedureDto } from '@bk/contracts';
import { useState } from 'react';
import { Figures } from './Fase';
import { Chip, GradeChip, Helpful, Kv, WaveScrap } from './Fase8';
import { PhaseFrame, usePhase } from './fasi-comune';
import type { PhasePageProps } from './fasi-pagine';

/**
 * La fase 9 «Le procedure» (docs/mockup/percorso-sinapsi/12-fase9; contratto n. 1 versione 2.2, art. 8, fase 9; ADR
 * 0178): una procedura aperta con passaggi, controlli, norma, traccia, programmi e indicatore; l'elenco di tutte; lo
 * scarto; le cose che aiuterebbero; le domande sulle varianti del settore. Come la fate oggi non si chiede.
 */

const LABEL_TONE: Record<string, 'cy' | 'mute' | 'gold'> = { 'lo chiede la legge': 'cy', 'secondo il consulente': 'gold' };

function ProcedureCard({ p }: { p: ProcedureDto }) {
  return (
    <article className="sn-panel lit" style={{ gap: 12, padding: '20px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <h2 className="sn-h2">{p.nome}</h2>
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {p.etichette.map((e) => (
            <Chip key={e} tone={LABEL_TONE[e] ?? 'mute'}>
              {e}
            </Chip>
          ))}
          <GradeChip g={p.grado} />
        </span>
      </div>
      <table className="sn-tbl wide">
        <tbody>
          <Kv k="Appartiene a">{p.appartiene_a}</Kv>
          <Kv k="Scopo">{p.scopo}</Kv>
          <Kv k="Chi la svolge · chi ne risponde">
            {p.chi_svolge} · {p.chi_risponde}
          </Kv>
          <Kv k="Cosa la avvia">{p.avvio}</Kv>
          <Kv k="Passaggi">
            <table className="sn-tbl">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>N.</th>
                  <th>Passaggio</th>
                  <th>Ruolo</th>
                </tr>
              </thead>
              <tbody>
                {p.passaggi.map((x) => (
                  <tr key={x.ordine}>
                    <td>{x.ordine}</td>
                    <td>{x.azione}</td>
                    <td>{x.ruolo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Kv>
          {p.controlli.length > 0 && <Kv k="Controlli">{p.controlli.join('; ')}</Kv>}
          {p.norma_ente && <Kv k="Norma ed ente">{p.norma_ente}</Kv>}
          <Kv k="Traccia">{p.traccia}</Kv>
          {p.programmi.length > 0 && <Kv k="Programmi e piattaforme">{p.programmi.join(' · ')}</Kv>}
          <Kv k="Indicatore">{p.indicatore}</Kv>
          {p.variante && <Kv k="Come la fate">{p.variante}</Kv>}
        </tbody>
      </table>
      {p.perche && (
        <details className="sn-why" style={{ fontSize: 13 }}>
          <summary style={{ cursor: 'pointer' }}>
            <b>Perché?</b> La procedura tipo e cosa ho adattato
          </summary>
          {p.perche}.
        </details>
      )}
    </article>
  );
}

export function Fase9Page({ next, onNext, sealing, stopped = false }: PhasePageProps) {
  const { q, reload, confirm, redo } = usePhase<PhaseNineDto>(9);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <PhaseFrame<PhaseNineDto & { scarto_pagina: PhaseNineDto['scarto'] }>
      // Lo scarto si mostra nella forma della pagina di prova, prima delle domande.
      d={q.data && { ...q.data, scarto: [], scarto_pagina: q.data.scarto }}
      error={q.error}
      eyebrow="Fase 9 · Le procedure"
      title={(d) => `Come si lavora in ${d.azienda ?? 'la tua azienda'}, passaggio per passaggio`}
      working="Preparo le procedure"
      questionsIntro="Solo dove la procedura tipo ha due varianti che cambiano chi la svolge o il programma. Come la fate oggi non te lo chiedo: se è diverso, correggi la procedura o scrivilo in «Parliamone»."
      questionsEmpty="Nessuna domanda: nessuna procedura ha nel settore due varianti che cambiano chi la svolge o il programma."
      scrapIntro=""
      next={next}
      onNext={onNext}
      sealing={sealing}
      stopped={stopped}
      onConfirm={confirm}
      onRedo={redo}
      reload={reload}
      beforeQuestions={(d) => (
        <>
          <WaveScrap list={d.scarto_pagina} intro="Le procedure che le fonti e le tue risposte non mostrano seguite oggi." missing="Le fonti non lo mostrano" />
          <Helpful d={d} onChange={reload} />
        </>
      )}
    >
      {(d) => {
        const shown = d.procedure.find((p) => p.id === open) ?? d.procedure.find((p) => p.etichette.includes('lo chiede la legge')) ?? d.procedure[0];
        return (
          <>
            <Figures list={d.numeri} />
            {shown && <ProcedureCard p={shown} />}
            <section className="sn-panel flush" style={{ gap: 0 }}>
              <div style={{ padding: '20px 24px 8px' }}>
                <h2 className="sn-h2">Tutte le procedure</h2>
              </div>
              <table className="sn-tbl wide">
                <thead>
                  <tr>
                    <th>Procedura</th>
                    <th>Appartiene a</th>
                    <th>Chi la svolge · chi ne risponde</th>
                    <th>Cosa la avvia</th>
                    <th>Traccia</th>
                    <th style={{ width: 110 }}>Grado</th>
                  </tr>
                </thead>
                <tbody>
                  {d.procedure.map((p) => (
                    <tr key={p.id} onClick={() => setOpen(p.id)} style={{ cursor: 'pointer', background: shown?.id === p.id ? 'rgba(60,240,255,.05)' : undefined }}>
                      <td>
                        <b>{p.nome}</b>
                      </td>
                      <td>{shown?.id === p.id && p.appartiene_breve === 'processo e obbligo' ? 'obbligo e processo, qui sopra' : p.appartiene_breve}</td>
                      <td>
                        {p.chi_svolge} · {p.chi_risponde}
                      </td>
                      <td>{p.avvio}</td>
                      <td>{p.traccia}</td>
                      <td>
                        <GradeChip g={p.grado} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="sn-note" style={{ padding: '12px 24px 18px' }}>
                Ognuna si apre come quella qui sopra, con passaggi, controlli, programmi e indicatore.
              </p>
            </section>
          </>
        );
      }}
    </PhaseFrame>
  );
}
